import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Aircraft, Transaction, Event, Company } from '../types/intellijetnet'
import { AircraftOverride, OffMarket, MarketCustomColumn, AircraftChangeInfo, AircraftTransaction, Option, TransactionOverride, MarketEMP } from '../types/database'
import { TemporaryAircraftStateService } from '../services/temporary-aircraft-state.service'
import { OverridesService } from '../services/overrides.service'
import { TransactionOverridesNewService } from '../services/transaction-overrides-new.service'
import { MarketCustomColumnsService } from '../services/market-custom-columns.service'
import { MarketEMPsService } from '../services/market-emps.service'
import { CompaniesService } from '../services/intellijetnet/companies.service'
import { detectBulkAircraftChanges } from '../utils/change-detection'
import { getEffectiveAircraftData } from '../utils/overrides'
import { useAircraftOptions } from './useAircraftOptions'
import { useOptionsByIds } from './useOptionsByIds'
import { useRelativeValue } from './useRelativeValue'
import { useOverrideRefresh } from '../contexts/OverrideRefreshContext'
import { NotesService } from '../services/notes.service'
import type { MarketTableProps, ForSaleTableProps, SoldTableProps, RemovedTableProps, CombinedAircraftData } from '../components/market-table/types'

/** Extended aircraft type with override fields */
export type EffectiveAircraft = Aircraft & {
  take_price?: number | null
  emp?: string | null
  tbo?: number | null
  pax?: number | null
  paint_date?: number | null
  interior_date?: number | null
}

/** Extended aircraft type for sold aircraft (includes options) */
export type EffectiveTransactionAircraft = EffectiveAircraft & {
  options?: string[] | null
}

export function useMarketTableData(props: MarketTableProps, expanded: boolean) {
  // --- Data state ---
  const [overrides, setOverrides] = useState<Map<number, AircraftOverride>>(new Map())
  const [customColumns, setCustomColumns] = useState<MarketCustomColumn[]>([])
  const [changeInfo, setChangeInfo] = useState<Map<number, AircraftChangeInfo>>(new Map())
  const [companyMap, setCompanyMap] = useState<Map<number, Company>>(new Map())
  const [excludedAircraftIds, setExcludedAircraftIds] = useState<Set<number>>(new Set())
  const [temporaryAircraftData, setTemporaryAircraftData] = useState<any[]>([])
  const [transactionOverrides, setTransactionOverrides] = useState<Map<string, TransactionOverride>>(new Map())
  const [marketEmps, setMarketEmps] = useState<MarketEMP[]>([])
  const [noteCounts, setNoteCounts] = useState<Map<number, number>>(new Map())
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [hasInitialData, setHasInitialData] = useState(false)
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const { refreshTrigger, optionsRefreshTrigger, triggerOptionsRefresh } = useOverrideRefresh()

  // --- Load temporary aircraft states ---
  useEffect(() => {
    const loadTemporaryStates = async () => {
      try {
        const modelId = parseInt(props.modelId, 10)
        if (!modelId) return

        const currentContainer = props.type === 'for-sale' ? 'for_sale' :
                               props.type === 'sold' ? 'sold' : 'removed_from_market'

        const excludedIds = await TemporaryAircraftStateService.getAircraftIdsToExclude(modelId, currentContainer)
        setExcludedAircraftIds(new Set(excludedIds))

        const tempData = await TemporaryAircraftStateService.getTemporaryAircraftForContainer(modelId, currentContainer)
        setTemporaryAircraftData(tempData)
      } catch (error) {
        // Error loading temporary aircraft states
      }
    }

    loadTemporaryStates()
  }, [props.modelId, props.type])

  // --- Aircraft list memoization ---
  const aircraftList = useMemo(() => {
    let apiAircraft: Aircraft[] = []

    if (props.type === 'for-sale') {
      apiAircraft = (props as any).aircraft || []
    } else {
      apiAircraft = (props as any).data?.map((item: any) => item.aircraft) || []
    }

    const offMarketAircraft = props.offMarketAircraft?.map(item => item.aircraft) || []
    return [...apiAircraft, ...offMarketAircraft]
  }, [props])

  // --- Stable model ID ---
  const modelId = useMemo(() => {
    return aircraftList.length > 0 ? aircraftList[0]?.model_id : null
  }, [aircraftList])

  // --- Aircraft IDs for fetching ---
  const aircraftIds = expanded ? [
    ...aircraftList.map(a => a.aircraft_id),
    ...temporaryAircraftData.map(tempData => tempData.aircraft_data?.aircraft_id).filter(Boolean)
  ] : []

  // --- Options hook ---
  const { data: options, loading: optionsLoading } = useAircraftOptions(aircraftIds, optionsRefreshTrigger)

  // --- Sold data for memoization ---
  const soldData = props.type === 'sold' ? props.data : null

  // --- Transaction option IDs ---
  const transactionOptionIds = useMemo(() => {
    if (!expanded || props.type !== 'sold' || !soldData) return []

    const optionIdSet = new Set<string>()
    soldData.forEach(({ aircraftTransaction }) => {
      if (aircraftTransaction?.options && Array.isArray(aircraftTransaction.options)) {
        aircraftTransaction.options.forEach(optionId => optionIdSet.add(optionId))
      }
    })
    transactionOverrides.forEach((override) => {
      if (override?.options && Array.isArray(override.options)) {
        override.options.forEach((optionId: string) => optionIdSet.add(optionId))
      }
    })
    return Array.from(optionIdSet)
  }, [expanded, props.type, soldData, transactionOverrides])

  const { data: transactionOptions } = useOptionsByIds(transactionOptionIds)

  // --- Fetch overrides ---
  useEffect(() => {
    if (expanded && aircraftIds.length > 0) {
      const fetchOverrides = async () => {
        try {
          const overrideMap = await OverridesService.getByAircraftIds(aircraftIds)
          if (overrideMap instanceof Map) {
            setOverrides(overrideMap)
          } else {
            setOverrides(new Map())
          }
        } catch (error) {
          setOverrides(new Map())
        }
      }
      fetchOverrides()
    } else {
      setOverrides(new Map())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, aircraftIds.join(','), refreshTrigger, temporaryAircraftData])

  // --- Fetch change info ---
  useEffect(() => {
    if (expanded && aircraftList.length > 0 && overrides.size > 0 && props.type !== 'sold') {
      const fetchChangeInfo = async () => {
        try {
          const offMarketList = props.offMarketAircraft?.map(item => item.offMarket) || []
          const changeMap = await detectBulkAircraftChanges(aircraftList, overrides, offMarketList)
          setChangeInfo(changeMap)
        } catch (error) {
          // Error fetching change info
        }
      }
      fetchChangeInfo()
    } else {
      setChangeInfo(new Map())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, aircraftList.length, overrides.size, props.offMarketAircraft?.length, props.type, refreshTrigger])

  // --- Fetch custom columns ---
  useEffect(() => {
    if (expanded && modelId) {
      const fetchCustomColumns = async () => {
        try {
          const columns = await MarketCustomColumnsService.getByModelId(modelId)
          setCustomColumns(columns)
        } catch (error) {
          // Error fetching custom columns
        }
      }
      fetchCustomColumns()
    } else {
      setCustomColumns([])
    }
  }, [expanded, modelId])

  // --- Fetch market EMPs ---
  useEffect(() => {
    if (expanded && modelId) {
      const fetchMarketEmps = async () => {
        try {
          const emps = await MarketEMPsService.getAllByModelId(modelId)
          setMarketEmps(emps)
        } catch (error) {
          // Error fetching market EMPs
        }
      }
      fetchMarketEmps()
    } else {
      setMarketEmps([])
    }
  }, [expanded, modelId])

  // --- Fetch note counts ---
  useEffect(() => {
    if (expanded && aircraftIds.length > 0) {
      const fetchNoteCounts = async () => {
        try {
          const counts = await NotesService.getNoteCountsByAircraftIds(aircraftIds)
          setNoteCounts(counts)
        } catch (error) {
          // Error fetching note counts - non-critical
        }
      }
      fetchNoteCounts()
    } else {
      setNoteCounts(new Map())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, aircraftIds.join(',')])

  // --- Fetch company data ---
  useEffect(() => {
    if (expanded && (overrides.size > 0 || (props.type === 'sold' && transactionOverrides.size > 0))) {
      const fetchCompanies = async () => {
        try {
          const companyIds = new Set<number>()

          overrides.forEach(override => {
            if (override.owner && !override.owner_custom) companyIds.add(override.owner)
            if (override.broker && !override.broker_custom) companyIds.add(override.broker)
          })

          if (props.type === 'sold') {
            const soldProps = props as SoldTableProps
            soldProps.data.forEach(({ aircraftTransaction }) => {
              if (aircraftTransaction) {
                if (aircraftTransaction.owner && !aircraftTransaction.owner_custom) {
                  companyIds.add(aircraftTransaction.owner)
                }
                if (aircraftTransaction.broker && !aircraftTransaction.broker_custom) {
                  companyIds.add(aircraftTransaction.broker)
                }

                const transactionOverride = transactionOverrides.get(aircraftTransaction.transaction_id)
                if (transactionOverride) {
                  if (transactionOverride.owner && !transactionOverride.owner_custom) {
                    companyIds.add(transactionOverride.owner)
                  }
                  if (transactionOverride.broker && !transactionOverride.broker_custom) {
                    companyIds.add(transactionOverride.broker)
                  }
                }
              }
            })
          }

          if (companyIds.size > 0) {
            const companies = await Promise.all(
              Array.from(companyIds).map(id => CompaniesService.getById(id))
            )

            const newCompanyMap = new Map<number, Company>()
            companies.forEach(company => {
              if (company) {
                newCompanyMap.set(company.company_id, company)
              }
            })
            setCompanyMap(newCompanyMap)
          }
        } catch (error) {
          // Error fetching company data
        }
      }
      fetchCompanies()
    } else {
      setCompanyMap(new Map())
    }
  }, [expanded, overrides, props.type, props, transactionOverrides])

  // --- Fetch transaction overrides (sold table only) ---
  useEffect(() => {
    if (expanded && props.type === 'sold' && soldData && soldData.length > 0) {
      const fetchTransactionOverrides = async () => {
        try {
          const transactionIds = soldData
            .map(({ aircraftTransaction }) => aircraftTransaction?.transaction_id)
            .filter((id): id is string => !!id)

          if (transactionIds.length === 0) {
            setTransactionOverrides(new Map())
            return
          }

          const overrideMap = await TransactionOverridesNewService.getByTransactionIds(transactionIds)
          setTransactionOverrides(overrideMap)
        } catch (error) {
          setTransactionOverrides(new Map())
        }
      }
      fetchTransactionOverrides()
    } else {
      setTransactionOverrides(new Map())
    }
  }, [expanded, props.type, soldData])

  // --- Helper: get effective transaction data ---
  const getEffectiveTransactionData = useCallback((aircraftTransaction: AircraftTransaction | null | undefined, transaction: Transaction | null | undefined) => {
    if (!aircraftTransaction) {
      return {
        asking_price: transaction?.asking_price || null,
        sale_price: (transaction as Transaction)?.sold_price || null
      }
    }

    return {
      asking_price: aircraftTransaction.asking_price,
      sale_price: aircraftTransaction.sale_price
    }
  }, [])

  // --- Prepare aircraft data for relative value calculation ---
  const aircraftData = (() => {
    let baseAircraftData: any[] = []

    if (props.type === 'for-sale') {
      baseAircraftData = props.aircraft.map(a => {
        const override = overrides.get(a.aircraft_id)
        return {
          aircraft_id: a.aircraft_id,
          yeardlv: override?.year_delivered ?? a.yeardlv,
          yearmfr: override?.year_manufactured ?? a.yearmfr,
          aftt: override?.aftt ?? a.aftt,
          transDate: null
        }
      })
    } else if (props.type === 'sold') {
      baseAircraftData = props.data.map(({ aircraft, transaction, aircraftTransaction }) => {
        const override = overrides.get(aircraft.aircraft_id)
        return {
          aircraft_id: aircraft.aircraft_id,
          yeardlv: override?.year_delivered ?? aircraft.yeardlv,
          yearmfr: override?.year_manufactured ?? aircraft.yearmfr,
          aftt: aircraftTransaction?.aftt || transaction?.est_aftt || override?.aftt || aircraft.aftt,
          transDate: aircraftTransaction?.transaction_date || transaction?.trans_date || null
        }
      })
    } else {
      baseAircraftData = props.data.map(({ aircraft }) => {
        const override = overrides.get(aircraft.aircraft_id)
        return {
          aircraft_id: aircraft.aircraft_id,
          yeardlv: override?.year_delivered ?? aircraft.yeardlv,
          yearmfr: override?.year_manufactured ?? aircraft.yearmfr,
          aftt: override?.aftt ?? aircraft.aftt,
          transDate: null
        }
      })
    }

    const tempAircraftData = temporaryAircraftData
      .filter(tempData => tempData.aircraft_data && props.type !== 'sold')
      .map(tempData => {
        const override = overrides.get(tempData.aircraft_data.aircraft_id)
        return {
          aircraft_id: tempData.aircraft_data.aircraft_id,
          yeardlv: override?.year_delivered ?? tempData.aircraft_data.yeardlv,
          yearmfr: override?.year_manufactured ?? tempData.aircraft_data.yearmfr,
          aftt: override?.aftt ?? tempData.aircraft_data.aftt,
          transDate: null
        }
      })

    const offMarketData = (props.offMarketAircraft || []).map(({ aircraft }) => {
      const override = overrides.get(aircraft.aircraft_id)
      return {
        aircraft_id: aircraft.aircraft_id,
        yeardlv: override?.year_delivered ?? aircraft.yeardlv,
        yearmfr: override?.year_manufactured ?? aircraft.yearmfr,
        aftt: override?.aftt ?? aircraft.aftt,
        transDate: null
      }
    })

    return [...baseAircraftData, ...tempAircraftData, ...offMarketData]
  })()

  // --- Merged options for relative value ---
  const mergedOptionsForRelativeValue = useMemo(() => {
    const merged = new Map<number, Option[]>(options)

    if (props.type === 'sold' && soldData) {
      soldData.forEach(({ aircraft, aircraftTransaction }) => {
        const transactionOverride = aircraftTransaction ? transactionOverrides.get(aircraftTransaction.transaction_id) : null
        const optionIds = transactionOverride?.options || aircraftTransaction?.options

        if (optionIds && Array.isArray(optionIds) && optionIds.length > 0) {
          const resolvedOptions: Option[] = optionIds
            .map((optionId: string) => transactionOptions.get(optionId))
            .filter((opt): opt is Option => opt !== undefined)

          if (resolvedOptions.length > 0) {
            merged.set(aircraft.aircraft_id, resolvedOptions)
          }
        }
      })
    }

    return merged
  }, [options, props.type, soldData, transactionOverrides, transactionOptions])

  // --- Relative value hook ---
  const numericModelId = parseInt(props.modelId, 10)
  const { values: relativeValues, breakdowns: relativeValueBreakdowns, missingABB, loading: valuesLoading } = useRelativeValue(
    numericModelId,
    aircraftData,
    mergedOptionsForRelativeValue
  )

  // --- Helper: get effective aircraft data with overrides ---
  const getEffectiveAircraft = useCallback((aircraft: Aircraft): EffectiveAircraft => {
    try {
      const override = overrides.get(aircraft.aircraft_id)
      const effectiveData = getEffectiveAircraftData(aircraft, override)
      return effectiveData
    } catch (error) {
      return { ...aircraft, take_price: null, emp: null, tbo: null, pax: null, paint_date: null, interior_date: null }
    }
  }, [overrides])

  // --- Helper: get effective aircraft data for sold aircraft (transaction snapshot + overrides) ---
  const getEffectiveTransactionAircraft = useCallback((
    aircraft: Aircraft,
    aircraftTransaction: AircraftTransaction | null | undefined
  ): EffectiveTransactionAircraft => {
    try {
      if (!aircraftTransaction) {
        return getEffectiveAircraft(aircraft)
      }

      const transactionOverride = transactionOverrides.get(aircraftTransaction.transaction_id)

      let effectiveData: EffectiveTransactionAircraft = {
        ...aircraft,
        take_price: null,
        emp: null,
        tbo: null,
        pax: null,
        paint_date: null,
        interior_date: null,
        options: null
      }

      // Apply transaction snapshot data
      if (aircraftTransaction.aftt !== null && aircraftTransaction.aftt !== undefined) {
        effectiveData.aftt = aircraftTransaction.aftt
      }
      if (aircraftTransaction.tbo !== null && aircraftTransaction.tbo !== undefined) {
        effectiveData.tbo = aircraftTransaction.tbo
      }
      if (aircraftTransaction.landings !== null && aircraftTransaction.landings !== undefined) {
        effectiveData.landings = aircraftTransaction.landings
      }
      if (aircraftTransaction.pax !== null && aircraftTransaction.pax !== undefined) {
        effectiveData.pax = aircraftTransaction.pax
      }
      if (aircraftTransaction.base_airport !== null && aircraftTransaction.base_airport !== undefined) {
        effectiveData.baseairport = aircraftTransaction.base_airport
      }
      if (aircraftTransaction.base_city !== null && aircraftTransaction.base_city !== undefined) {
        effectiveData.basecity = aircraftTransaction.base_city
      }
      if (aircraftTransaction.base_state !== null && aircraftTransaction.base_state !== undefined) {
        effectiveData.basestate = aircraftTransaction.base_state
      }
      if (aircraftTransaction.base_country !== null && aircraftTransaction.base_country !== undefined) {
        effectiveData.basecountry = aircraftTransaction.base_country
      }
      if (aircraftTransaction.status !== null && aircraftTransaction.status !== undefined) {
        effectiveData.status = aircraftTransaction.status
      }
      if (aircraftTransaction.damage_history !== null && aircraftTransaction.damage_history !== undefined) {
        effectiveData.damagehistoryyn = aircraftTransaction.damage_history
      }
      if (aircraftTransaction.emp !== null && aircraftTransaction.emp !== undefined) {
        effectiveData.emp = aircraftTransaction.emp
      }
      if (aircraftTransaction.asking_price !== null && aircraftTransaction.asking_price !== undefined) {
        effectiveData.asking = aircraftTransaction.asking_price?.toString() || null
      }
      if (aircraftTransaction.take_price !== null && aircraftTransaction.take_price !== undefined) {
        effectiveData.take_price = aircraftTransaction.take_price
      }
      if (aircraftTransaction.options !== null && aircraftTransaction.options !== undefined) {
        effectiveData.options = aircraftTransaction.options
      }
      if (aircraftTransaction.paint_date !== null && aircraftTransaction.paint_date !== undefined) {
        effectiveData.paint_date = aircraftTransaction.paint_date
      }
      if (aircraftTransaction.interior_date !== null && aircraftTransaction.interior_date !== undefined) {
        effectiveData.interior_date = aircraftTransaction.interior_date
      }

      // Apply transaction overrides on top of snapshot data
      if (transactionOverride) {
        if (transactionOverride.aftt !== null && transactionOverride.aftt !== undefined) {
          effectiveData.aftt = transactionOverride.aftt
        }
        if (transactionOverride.tbo !== null && transactionOverride.tbo !== undefined) {
          effectiveData.tbo = transactionOverride.tbo
        }
        if (transactionOverride.landings !== null && transactionOverride.landings !== undefined) {
          effectiveData.landings = transactionOverride.landings
        }
        if (transactionOverride.pax !== null && transactionOverride.pax !== undefined) {
          effectiveData.pax = transactionOverride.pax
        }
        if (transactionOverride.base_airport !== null && transactionOverride.base_airport !== undefined) {
          effectiveData.baseairport = transactionOverride.base_airport
        }
        if (transactionOverride.base_city !== null && transactionOverride.base_city !== undefined) {
          effectiveData.basecity = transactionOverride.base_city
        }
        if (transactionOverride.base_state !== null && transactionOverride.base_state !== undefined) {
          effectiveData.basestate = transactionOverride.base_state
        }
        if (transactionOverride.base_country !== null && transactionOverride.base_country !== undefined) {
          effectiveData.basecountry = transactionOverride.base_country
        }
        if (transactionOverride.status !== null && transactionOverride.status !== undefined) {
          effectiveData.status = transactionOverride.status
        }
        if (transactionOverride.damage_history !== null && transactionOverride.damage_history !== undefined) {
          effectiveData.damagehistoryyn = transactionOverride.damage_history
        }
        if (transactionOverride.emp !== null && transactionOverride.emp !== undefined) {
          effectiveData.emp = transactionOverride.emp
        }
        if (transactionOverride.asking_price !== null && transactionOverride.asking_price !== undefined) {
          effectiveData.asking = transactionOverride.asking_price?.toString() || null
        }
        if (transactionOverride.take_price !== null && transactionOverride.take_price !== undefined) {
          effectiveData.take_price = transactionOverride.take_price
        }
        if (transactionOverride.options !== null && transactionOverride.options !== undefined) {
          effectiveData.options = transactionOverride.options
        }
        if (transactionOverride.paint_date !== null && transactionOverride.paint_date !== undefined) {
          effectiveData.paint_date = transactionOverride.paint_date
        }
        if (transactionOverride.interior_date !== null && transactionOverride.interior_date !== undefined) {
          effectiveData.interior_date = transactionOverride.interior_date
        }
      }

      return effectiveData
    } catch (error) {
      return { ...aircraft, take_price: null, emp: null, tbo: null, pax: null, paint_date: null, interior_date: null }
    }
  }, [transactionOverrides, getEffectiveAircraft])

  // --- Combined aircraft data ---
  const combinedAircraft: CombinedAircraftData[] = useMemo(() => {
    const combined: CombinedAircraftData[] = []

    const offMarketAircraftIds = new Set(
      (props.offMarketAircraft || []).map(item => item.aircraft.aircraft_id)
    )

    if (props.type === 'for-sale') {
      props.aircraft.forEach(aircraft => {
        if (!excludedAircraftIds.has(aircraft.aircraft_id) &&
            !offMarketAircraftIds.has(aircraft.aircraft_id)) {
          combined.push({ aircraft, source: 'api' })
        }
      })
    } else if (props.type === 'sold') {
      props.data.forEach(({ aircraft, transaction, aircraftTransaction }) => {
        combined.push({
          aircraft,
          source: aircraftTransaction ? 'snapshot' : 'api',
          transaction,
          aircraftTransaction
        })
      })
    } else if (props.type === 'removed') {
      props.data.forEach(({ aircraft, event }) => {
        if (!excludedAircraftIds.has(aircraft.aircraft_id) &&
            !offMarketAircraftIds.has(aircraft.aircraft_id)) {
          combined.push({ aircraft, source: 'api', event })
        }
      })
    }

    if (props.offMarketAircraft) {
      props.offMarketAircraft.forEach(({ aircraft, offMarket }) => {
        if (!excludedAircraftIds.has(aircraft.aircraft_id)) {
          combined.push({
            aircraft,
            source: 'offMarket',
            offMarket,
            transaction: props.type === 'sold' ? null : undefined,
            event: props.type === 'removed' ? null : undefined
          })
        }
      })
    }

    temporaryAircraftData.forEach(tempData => {
      if (tempData.aircraft_data && props.type !== 'sold') {
        const aircraftId = tempData.aircraft_data.aircraft_id

        if (props.type === 'for-sale') {
          const forSaleProps = props as ForSaleTableProps
          if (forSaleProps.soldAircraftIds?.has(aircraftId)) {
            return
          }
        }

        if (props.type === 'removed') {
          const removedProps = props as RemovedTableProps
          if (removedProps.soldAircraftIds?.has(aircraftId)) {
            return
          }
        }

        const mockTransaction: Transaction | undefined = undefined

        const mockEvent: Event | undefined = props.type === 'removed' ? {
          event_id: 'temp-' + tempData.aircraft_data.aircraft_id,
          aircraft_id: tempData.aircraft_data.aircraft_id,
          date: new Date().toISOString(),
          subject: 'Manual Move',
          description: 'Aircraft manually moved to removed from market',
          company_id: null,
          contact_id: null,
          model_id: tempData.aircraft_data.model_id || 0,
          sync_timestamp: new Date().toISOString(),
          is_active: true
        } : undefined

        combined.push({
          aircraft: tempData.aircraft_data,
          source: 'temporary',
          transaction: mockTransaction,
          event: mockEvent
        })
      }
    })

    return combined
  }, [props, excludedAircraftIds, temporaryAircraftData])


  // --- Track initial data ---
  useEffect(() => {
    if (options.size > 0 || relativeValues.size > 0) {
      setHasInitialData(true)
    }
  }, [options.size, relativeValues.size])

  // --- Track loading state with debouncing ---
  useEffect(() => {
    if (expanded) {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }

      const isLoading = optionsLoading || valuesLoading

      if (isLoading) {
        if (!hasInitialData) {
          setIsDataLoading(true)
        }
      } else {
        loadingTimeoutRef.current = setTimeout(() => {
          setIsDataLoading(false)
        }, 100)
      }
    }

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
    }
  }, [expanded, optionsLoading, valuesLoading, hasInitialData])

  // --- Set loading state when expanded changes ---
  useEffect(() => {
    if (expanded && !hasInitialData && aircraftList.length > 0) {
      setIsDataLoading(true)
    }
  }, [expanded, hasInitialData, aircraftList.length])

  // --- Return all data, state, and helpers ---
  return {
    // Data state
    overrides,
    customColumns,
    changeInfo,
    companyMap,
    transactionOverrides,
    marketEmps,
    noteCounts,
    options,
    transactionOptions,
    relativeValues,
    relativeValueBreakdowns,
    missingABB,
    valuesLoading,
    optionsLoading,
    isDataLoading,
    hasInitialData,

    // Computed data
    aircraftList,
    modelId,
    combinedAircraft,

    // Helper functions
    getEffectiveAircraft,
    getEffectiveTransactionAircraft,
    getEffectiveTransactionData,

    // State setters (needed by editing handlers)
    setOverrides,
    setTransactionOverrides,
    setCompanyMap,
    setNoteCounts,

    // Refresh triggers
    triggerOptionsRefresh,
    optionsRefreshTrigger,
    refreshTrigger,
  }
}